import { useCallback, useMemo, useSyncExternalStore } from "react";

import {
  scheduleShopifyLiveCommerceCartSync,
  toShopifyCartLineInputs,
} from "./syncCart";

import type { ProductData } from "@spwn/types/firebase/firestore";

type LiveCommerceCartItem = ProductData & { count: number };

type EventCart = {
  items: LiveCommerceCartItem[];
  listeners: Set<() => void>;
};

/**
 * 配信画面のカートはイベント単位でモジュールスコープに持つ。
 * 画面を閉じて開き直してもカートが残り、複数のコンポーネントから同じ内容が見える。
 */
const carts = new Map<string, EventCart>();

const EMPTY_ITEMS: LiveCommerceCartItem[] = [];

const getCart = (eventId: string): EventCart => {
  const existing = carts.get(eventId);
  if (existing !== undefined) return existing;
  const created: EventCart = { items: EMPTY_ITEMS, listeners: new Set() };
  carts.set(eventId, created);
  return created;
};

/** 中身を差し替えて、購読者へ通知し、Shopify への同期を予約する */
const setItems = (eventId: string, items: LiveCommerceCartItem[]): void => {
  const cart = getCart(eventId);
  cart.items = items;
  cart.listeners.forEach((listener) => listener());
  scheduleShopifyLiveCommerceCartSync({
    eventId,
    lines: toShopifyCartLineInputs(items),
  });
};

export const getLiveCommerceCartItems = (
  eventId: string
): LiveCommerceCartItem[] => getCart(eventId).items;

const changeCount = (
  eventId: string,
  product: ProductData,
  delta: number
): void => {
  const productId = product._id;
  if (productId === undefined) return;

  const { items } = getCart(eventId);
  const current = items.find((item) => item._id === productId);
  const count = (current?.count ?? 0) + delta;

  if (count <= 0) {
    if (current !== undefined) {
      setItems(
        eventId,
        items.filter((item) => item._id !== productId)
      );
    }
    return;
  }
  if (current === undefined) {
    setItems(eventId, [...items, { ...product, count }]);
    return;
  }
  setItems(
    eventId,
    items.map((item) => {
      return item._id === productId ? { ...item, count } : item;
    })
  );
};

export const incrementLiveCommerceCartItem = (
  eventId: string,
  product: ProductData
): void => changeCount(eventId, product, 1);

export const decrementLiveCommerceCartItem = (
  eventId: string,
  product: ProductData
): void => changeCount(eventId, product, -1);

export const removeLiveCommerceCartItem = (
  eventId: string,
  product: ProductData
): void => {
  const { items } = getCart(eventId);
  if (!items.some((item) => item._id === product._id)) return;
  setItems(
    eventId,
    items.filter((item) => item._id !== product._id)
  );
};

/** カートに無い商品だけ1つ入れる。すでに入っていれば数量は変えない */
export const addLiveCommerceCartItemIfAbsent = (
  eventId: string,
  product: ProductData
): void => {
  if (getCart(eventId).items.some((item) => item._id === product._id)) return;
  changeCount(eventId, product, 1);
};

export const clearLiveCommerceCart = (eventId: string): void => {
  if (getCart(eventId).items.length === 0) return;
  setItems(eventId, EMPTY_ITEMS);
};

const subscribe = (eventId: string, listener: () => void): (() => void) => {
  const cart = getCart(eventId);
  cart.listeners.add(listener);
  return () => {
    cart.listeners.delete(listener);
  };
};

/** eventId が未確定の間は空のカートとして扱い、何も操作できない */
export const useShopifyLiveCommerceCart = (eventId: string | undefined) => {
  const subscribeToCart = useCallback(
    (listener: () => void) => {
      if (eventId === undefined) return () => undefined;
      return subscribe(eventId, listener);
    },
    [eventId]
  );
  const getSnapshot = useCallback(() => {
    if (eventId === undefined) return EMPTY_ITEMS;
    return getCart(eventId).items;
  }, [eventId]);
  const items = useSyncExternalStore(subscribeToCart, getSnapshot);

  return useMemo(() => {
    const totalNum = items.reduce((sum, item) => sum + item.count, 0);
    const run =
      (operation: (eventId: string, product: ProductData) => void) =>
      (product: ProductData) => {
        if (eventId !== undefined) operation(eventId, product);
      };
    return {
      items,
      totalNum,
      increment: run(incrementLiveCommerceCartItem),
      decrement: run(decrementLiveCommerceCartItem),
      remove: run(removeLiveCommerceCartItem),
      addIfAbsent: run(addLiveCommerceCartItemIfAbsent),
      clear: () => {
        if (eventId !== undefined) clearLiveCommerceCart(eventId);
      },
    };
  }, [eventId, items]);
};
